// intentGuards.ts

export type LightweightIntent =
  | 'open_create_content_flow'
  | 'open_edit_product_flow'
  | 'open_build_funnel_flow'
  | 'open_create_portrait_flow'
  | 'open_create_flyer_flow'
  | 'open_create_publication_flow'
  | 'open_create_ads_campaign_flow'
  | 'open_latest_publication_carousel'
  | 'navigate'
  | 'unknown'

export type LightweightIntentConfidence = 'high' | 'medium' | 'low'

export type LightweightIntentResult = {
  intent: LightweightIntent
  confidence: LightweightIntentConfidence
  shouldAutoOpen: boolean
  matched?: string
}

/**
 * Normaliza texto para detección simple.
 * Mantiene espacios, pero elimina ruido básico.
 */
function normalizeIntentText(input: string) {
  return String(input || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // quita acentos
    .replace(/[¿?¡!.,;:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function matchesAny(text: string, patterns: RegExp[]) {
  for (const pattern of patterns) {
    if (pattern.test(text)) return pattern.source
  }
  return null
}

/**
 * Palabras que indican que el usuario está hablando, planeando,
 * configurando o preguntando; no necesariamente pidiendo abrir un flujo.
 */
const DISCUSSION_GUARDS = [
  /\bcomo\b/,
  /\bque es\b/,
  /\bexplicame\b/,
  /\bexplica\b/,
  /\bayudame a\b/,
  /\bquiero aprender\b/,
  /\bquiero saber\b/,
  /\bestoy configurando\b/,
  /\bestoy creando\b/,
  /\bestoy armando\b/,
  /\bestamos creando\b/,
  /\bestamos armando\b/,
  /\bmi agente\b/,
  /\bun agente\b/,
  /\bgpt\b/,
  /\bspecialized agent\b/,
  /\bagente especializado\b/,
  /\bniche bending\b/,
  /\bestrategia\b/,
]

function looksLikeDiscussion(text: string) {
  return DISCUSSION_GUARDS.some((pattern) => pattern.test(text))
}

/**
 * Verbos que sí suelen indicar acción de abrir/iniciar flujo.
 */
const ACTION_VERBS_ES =
  '(abre|abrir|inicia|iniciar|empieza|empezar|lanza|lanzar|ve a|llevame a|mandame a|quiero abrir|quiero iniciar|quiero empezar|quiero crear|quiero hacer|necesito crear|necesito hacer|vamos a abrir|vamos a iniciar|vamos a empezar|vamos a crear|vamos a hacer|crear|hacer)'

const ACTION_VERBS_EN =
  '(open|start|launch|go to|take me to|send me to|i want to open|i want to start|let us open|let us start|let\\s open|let\\s start)'

/**
 * Patrones HIGH confidence:
 * Instrucciones puntuales tipo “abre el flujo de...”
 */
const CREATE_CONTENT_HIGH = [
  new RegExp(`^${ACTION_VERBS_ES}\\s+(el\\s+)?(flujo|creador|editor|modulo|herramienta)\\s+(de\\s+)?(crear\\s+)?(contenido|content creator)$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(crear\\s+)?(nuevo\\s+)?contenido$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(un\\s+)?(nuevo\\s+)?(proyecto\\s+de\\s+)?contenido$`),
  new RegExp(`^${ACTION_VERBS_EN}\\s+(the\\s+)?(content creator|content flow|content editor)$`),
  new RegExp(`^${ACTION_VERBS_EN}\\s+(a\\s+)?(new\\s+)?(content project|content)$`),
]

const EDIT_PRODUCT_HIGH = [
  new RegExp(`^${ACTION_VERBS_ES}\\s+(el\\s+)?(flujo|editor|modulo|herramienta)\\s+(de\\s+)?(editar\\s+)?producto$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(editar|cambiar|modificar)\\s+(un\\s+)?producto$`),
  new RegExp(`^${ACTION_VERBS_EN}\\s+(the\\s+)?(product editor|edit product flow)$`),
  new RegExp(`^${ACTION_VERBS_EN}\\s+(edit|change|modify)\\s+(a\\s+)?product$`),
]

const BUILD_FUNNEL_HIGH = [
  new RegExp(`^${ACTION_VERBS_ES}\\s+(el\\s+)?(flujo|constructor|builder|modulo|herramienta)\\s+(de\\s+)?(crear\\s+|construir\\s+|armar\\s+)?(embudo|funnel)$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(crear|construir|armar)\\s+(un\\s+)?(nuevo\\s+)?(embudo|funnel)$`),

  /^(crear|construir|armar)\s+(un\s+)?(nuevo\s+)?(embudo|funnel)$/,

  new RegExp(`^${ACTION_VERBS_EN}\\s+(the\\s+)?(funnel builder|build funnel flow|funnel flow)$`),
  new RegExp(`^${ACTION_VERBS_EN}\\s+(build|create)\\s+(a\\s+)?(new\\s+)?funnel$`),

  /^(build|create)\s+(a\s+)?(new\s+)?funnel$/,
]

const CREATE_PORTRAIT_HIGH = [
  new RegExp(`^${ACTION_VERBS_ES}\\s+(el\\s+)?(flujo|creador|modulo|herramienta)\\s+(de\\s+)?(crear\\s+)?(retrato|portrait|imagen profesional)$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(crear|generar|hacer)\\s+(un\\s+)?(retrato|portrait|imagen profesional)$`),
  new RegExp(`^${ACTION_VERBS_EN}\\s+(the\\s+)?(portrait creator|portrait flow|professional portrait flow)$`),
  new RegExp(`^${ACTION_VERBS_EN}\\s+(create|generate|make)\\s+(a\\s+)?(portrait|professional portrait)$`),
]

const CREATE_FLYER_HIGH = [
  new RegExp(`^${ACTION_VERBS_ES}\\s+(el\\s+)?(flujo|creador|modulo|herramienta)\\s+(de\\s+)?(crear\\s+)?flyer$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(crear\\s+|generar\\s+|hacer\\s+|diseñar\\s+)?(un\\s+)?(flyer|volante|cartel|poster|póster|folleto)$`),
  new RegExp(`^${ACTION_VERBS_EN}\\s+(create\\s+|generate\\s+|make\\s+|design\\s+)?(a\\s+)?(flyer|poster)$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(crear|generar|hacer)\\s+(un\\s+)?flyer$`),
  new RegExp(`^${ACTION_VERBS_EN}\\s+(the\\s+)?(flyer creator|flyer flow)$`),
  new RegExp(`^${ACTION_VERBS_EN}\\s+(create|generate|make)\\s+(a\\s+)?flyer$`),
]

const NAVIGATE_HIGH = [
  /^ve a\s+.+/,
  /^llevame a\s+.+/,
  /^mandame a\s+.+/,
  /^abrir pagina\s+.+/,
  /^abre la pagina\s+.+/,
  /^abre pagina\s+.+/,
  /^go to\s+.+/,
  /^take me to\s+.+/,
  /^open page\s+.+/,
  /^open the page\s+.+/,
]

const OPEN_LATEST_PUBLICATION_CAROUSEL_HIGH = [
  /\b(muestrame|muestra|mostrar|abre|abrir|ver|quiero ver|editar|quiero editar)\b.*\b(mi\s+)?(ultimo|ultima|mas reciente|reciente)\b.*\b(carrusel|carousel)\b/,
  /\b(muestrame|muestra|mostrar|abre|abrir|ver|quiero ver|editar|quiero editar)\b.*\b(mi\s+)?(ultimo|ultima|mas reciente|reciente)\b.*\b(publicacion|publicaciones|post)\b.*\b(carrusel|carousel)\b/,
  /\b(muestrame|muestra|mostrar|abre|abrir|ver|quiero ver|editar|quiero editar)\b.*\b(carrusel|carousel)\b.*\b(ultimo|ultima|mas reciente|reciente|creado|creada|generado|generada)\b/,
  /\b(muestrame|muestra|mostrar|abre|abrir|ver|quiero ver|editar|quiero editar)\b.*\b(publicacion|post)\b.*\b(ultimo|ultima|mas reciente|reciente)\b.*\b(generado|generada|creado|creada)\b/,
  /\b(carrusel|carousel)\b.*\b(ultimo|ultima|mas reciente|reciente)\b.*\b(creado|creada|generado|generada)\b/,
]

const OPEN_LATEST_PUBLICATION_CAROUSEL_BLOCKERS = [
  /\bembudo\b/,
  /\bfunnel\b/,
  /\bcurso\b/,
  /\bcursos\b/,
  /\bleccion\b/,
  /\blección\b/,
  /\bmodulo\b/,
  /\bmódulo\b/,
  /\bcampana\b/,
  /\bcampaña\b/,
  /\bads\b/,
  /\banuncios\b/,
  /\bresultados\b/,
  /\breporte\b/,
  /\banalitica\b/,
  /\banalítica\b/,
  /\bmetricas\b/,
  /\bmétricas\b/,
]

/**
 * Patrones MEDIUM confidence:
 * Podrían ser acción, pero conviene confirmar antes de abrir.
 */
const CREATE_CONTENT_MEDIUM = [
  /\bquiero crear contenido\b/,
  /\bnecesito crear contenido\b/,
  /\bvamos a crear contenido\b/,
  /\bhazme contenido\b/,
  /\bquiero crear (un\s+)?(nuevo\s+)?video\b/,
  /\bquiero hacer (un\s+)?(nuevo\s+)?video\b/,
  /\bnecesito crear (un\s+)?(nuevo\s+)?video\b/,
  /\bvamos a crear (un\s+)?(nuevo\s+)?video\b/,
  /\bcrear (un\s+)?(nuevo\s+)?video\b/,
  /\bhacer (un\s+)?(nuevo\s+)?video\b/,
  /\bcrear video\b/,
  /\bcreate content\b/,
  /\bmake content\b/,
  /\bcreate (a\s+)?(new\s+)?video\b/,
  /\bmake (a\s+)?(new\s+)?video\b/,
]

const EDIT_PRODUCT_MEDIUM = [
  /\bquiero editar producto\b/,
  /\bnecesito editar producto\b/,
  /\beditar producto\b/,
  /\bedit product\b/,
]

const BUILD_FUNNEL_MEDIUM = [
  /\bquiero crear (un\s+)?(nuevo\s+)?(embudo|funnel)\b/,
  /\bquiero construir (un\s+)?(nuevo\s+)?(embudo|funnel)\b/,
  /\bquiero armar (un\s+)?(nuevo\s+)?(embudo|funnel)\b/,

  /\bcrear (un\s+)?(nuevo\s+)?(embudo|funnel)\b/,
  /\bconstruir (un\s+)?(nuevo\s+)?(embudo|funnel)\b/,
  /\barmar (un\s+)?(nuevo\s+)?(embudo|funnel)\b/,

  /\bcrear embudo desde plantilla\b/,
  /\bcrear un embudo desde plantilla\b/,
  /\bcrear funnel desde plantilla\b/,
  /\bcrear un funnel desde plantilla\b/,

  /\bbuild (a\s+)?(new\s+)?funnel\b/,
  /\bcreate (a\s+)?(new\s+)?funnel\b/,
]

const CREATE_PORTRAIT_MEDIUM = [
  /\bquiero crear retrato\b/,
  /\bcrear retrato\b/,
  /\bcrear imagen profesional\b/,
  /\bprofessional portrait\b/,
  /\bcreate portrait\b/,
]

const CREATE_FLYER_MEDIUM = [
  // Español neutro / México / Colombia
  /\bquiero crear (un\s+)?flyer\b/,
  /\bquiero hacer (un\s+)?flyer\b/,
  /\bquiero generar (un\s+)?flyer\b/,
  /\bnecesito crear (un\s+)?flyer\b/,
  /\bnecesito hacer (un\s+)?flyer\b/,
  /\bnecesito generar (un\s+)?flyer\b/,
  /\bcrear (un\s+)?flyer\b/,
  /\bhacer (un\s+)?flyer\b/,
  /\bgenerar (un\s+)?flyer\b/,
  /\bhazme (un\s+)?flyer\b/,
  /\bhaceme (un\s+)?flyer\b/,
  /\bme haces (un\s+)?flyer\b/,
  /\bme creas (un\s+)?flyer\b/,
  /\bme generas (un\s+)?flyer\b/,

  // Variantes comunes: volante / cartel / poster / anuncio
  /\bquiero crear (un\s+)?(volante|cartel|poster|póster)\b/,
  /\bquiero hacer (un\s+)?(volante|cartel|poster|póster)\b/,
  /\bquiero generar (un\s+)?(volante|cartel|poster|póster)\b/,
  /\bnecesito (un\s+)?(volante|cartel|poster|póster)\b/,
  /\bhazme (un\s+)?(volante|cartel|poster|póster)\b/,
  /\bhaceme (un\s+)?(volante|cartel|poster|póster)\b/,
  /\bme haces (un\s+)?(volante|cartel|poster|póster)\b/,
  /\bme creas (un\s+)?(volante|cartel|poster|póster)\b/,

  // España
  /\bquiero crear (un\s+)?folleto\b/,
  /\bquiero hacer (un\s+)?folleto\b/,
  /\bnecesito (un\s+)?folleto\b/,
  /\bhazme (un\s+)?folleto\b/,

  // Argentina / voseo
  /\bquiero que me hagas (un\s+)?flyer\b/,
  /\bquiero que me armes (un\s+)?flyer\b/,
  /\barmame (un\s+)?flyer\b/,
  /\bármame (un\s+)?flyer\b/,
  /\bhaceme (un\s+)?flyer\b/,
  /\bcreame (un\s+)?flyer\b/,
  /\bcréame (un\s+)?flyer\b/,
  /\bgenerame (un\s+)?flyer\b/,
  /\bgénerame (un\s+)?flyer\b/,

  // Inglés
  /\bi want to create (a\s+)?flyer\b/,
  /\bi want to make (a\s+)?flyer\b/,
  /\bi need (a\s+)?flyer\b/,
  /\bcreate (a\s+)?flyer\b/,
  /\bmake (a\s+)?flyer\b/,
  /\bgenerate (a\s+)?flyer\b/,
  /\bmake me (a\s+)?flyer\b/,
  /\bcreate me (a\s+)?flyer\b/,
  /\bdesign (a\s+)?flyer\b/,
  /\bi want to design (a\s+)?flyer\b/,
  /\bi need (a\s+)?(poster|promotional graphic)\b/,
  /\bcreate (a\s+)?(poster|promotional graphic)\b/,
  /\bmake (a\s+)?(poster|promotional graphic)\b/,
]

const CREATE_ADS_CAMPAIGN_HIGH = [
  new RegExp(`^${ACTION_VERBS_ES}\\s+(una\\s+)?(nueva\\s+)?(campana|campaña|campana\\s+publicitaria|campaña\\s+publicitaria)$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(ads|anuncios|publicidad)$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(una\\s+)?(campana|campaña|ads|anuncios)\\s+(para\\s+)?(meta|facebook|instagram|facebook\\s+ads|meta\\s+ads)$`),

  /\bquiero crear (una\s+)?(nueva\s+)?(campana|campaña)\b/,
  /\bquiero lanzar (una\s+)?(nueva\s+)?(campana|campaña)\b/,
  /\bquiero hacer (una\s+)?(nueva\s+)?(campana|campaña)\b/,
  /\bnecesito crear (una\s+)?(nueva\s+)?(campana|campaña)\b/,
  /\bnecesito lanzar (una\s+)?(nueva\s+)?(campana|campaña)\b/,

  /\bpromocionar (mi\s+)?(embudo|funnel|producto|reto|curso|comunidad|evento)\b/,
  /\bquiero promocionar (mi\s+)?(embudo|funnel|producto|reto|curso|comunidad|evento)\b/,
  /\bnecesito promocionar (mi\s+)?(embudo|funnel|producto|reto|curso|comunidad|evento)\b/,

  /\bcrear ads\b/,
  /\bhacer ads\b/,
  /\blanzar ads\b/,
  /\bcrear anuncios\b/,
  /\bhacer anuncios\b/,
  /\blanzar anuncios\b/,
  /\bmeta ads\b/,
  /\bfacebook ads\b/,
  /\binstagram ads\b/,

  /\bconseguir leads con anuncios\b/,
  /\bconseguir prospectos con anuncios\b/,
  /\bvender con anuncios\b/,
  /\bvender con ads\b/,

  new RegExp(`^${ACTION_VERBS_EN}\\s+(a\\s+)?(new\\s+)?(campaign|ad\\s+campaign|ads\\s+campaign)$`),
  /\bi want to create (a\s+)?(new\s+)?(campaign|ad campaign)\b/,
  /\bi want to launch (a\s+)?(new\s+)?(campaign|ad campaign)\b/,
  /\bpromote (my\s+)?(funnel|product|course|challenge|community|event)\b/,
  /\bcreate ads\b/,
  /\blaunch ads\b/,
  /\bfacebook ads\b/,
  /\bmeta ads\b/,
  /\binstagram ads\b/,
]

const CREATE_PUBLICATION_HIGH = [
  new RegExp(`^${ACTION_VERBS_ES}\\s+(una\\s+)?(nueva\\s+)?(publicacion|publicación|post|contenido\\s+para\\s+redes|contenido\\s+de\\s+redes)$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(un\\s+)?(post|contenido)\\s+(para\\s+)?(instagram|facebook|tiktok|redes\\s+sociales|redes)$`),
  new RegExp(`^${ACTION_VERBS_ES}\\s+(una\\s+)?(publicacion|publicación)\\s+(para\\s+)?(instagram|facebook|tiktok|redes\\s+sociales|redes)$`),

  /\bquiero crear (un\s+)?(nuevo\s+)?post (para|de) (instagram|facebook|tiktok|redes sociales|redes)\b/,
  /\bquiero hacer (un\s+)?(nuevo\s+)?post (para|de) (instagram|facebook|tiktok|redes sociales|redes)\b/,
  /\bcrear (un\s+)?(nuevo\s+)?post (para|de) (instagram|facebook|tiktok|redes sociales|redes)\b/,
  /\bhacer (un\s+)?(nuevo\s+)?post (para|de) (instagram|facebook|tiktok|redes sociales|redes)\b/,

  /\bquiero crear (una\s+)?(nueva\s+)?(publicacion|publicación) (para|de) (instagram|facebook|tiktok|redes sociales|redes)\b/,
  /\bquiero hacer (una\s+)?(nueva\s+)?(publicacion|publicación) (para|de) (instagram|facebook|tiktok|redes sociales|redes)\b/,
  /\bcrear (una\s+)?(nueva\s+)?(publicacion|publicación) (para|de) (instagram|facebook|tiktok|redes sociales|redes)\b/,
  /\bhacer (una\s+)?(nueva\s+)?(publicacion|publicación) (para|de) (instagram|facebook|tiktok|redes sociales|redes)\b/,

  /\bcrear una nueva publicacion\b/,
  /\bcrear una nueva publicación\b/,
  /\bcrear un nuevo post\b/,
  /\bcrear contenido para redes\b/,
  /\bcrear contenido de redes\b/,
  /\bquiero publicar algo\b/,
  /\bquiero hacer una publicacion\b/,
  /\bquiero hacer una publicación\b/,
  /\bquiero hacer un post\b/,
  /\bcrea un post para redes\b/,
  /\bcrea una publicacion para redes\b/,
  /\bcrea una publicación para redes\b/,

  new RegExp(`^${ACTION_VERBS_EN}\\s+(a\\s+)?(new\\s+)?(post|social\\s+post|publication|social\\s+media\\s+post)$`),
  /\bcreate a social media post\b/,
  /\bcreate a post for social media\b/,
  /\bi want to publish something\b/,
]

const CREATE_ADS_CAMPAIGN_MEDIUM = [
  /\bcampana publicitaria\b/,
  /\bcampaña publicitaria\b/,
  /\bcampana para facebook\b/,
  /\bcampaña para facebook\b/,
  /\bcampana para instagram\b/,
  /\bcampaña para instagram\b/,
  /\bcampana para meta\b/,
  /\bcampaña para meta\b/,
  /\banuncios para facebook\b/,
  /\banuncios para instagram\b/,
  /\banuncios de meta\b/,
  /\bpublicidad en facebook\b/,
  /\bpublicidad en instagram\b/,
  /\bpublicidad en meta\b/,
  /\bpromocionar embudo\b/,
  /\bpromocionar funnel\b/,
  /\bpromocionar producto\b/,
  /\bpromocionar reto\b/,
  /\bpromocionar curso\b/,
  /\bpromocionar comunidad\b/,
  /\bconseguir leads\b.*\banuncios\b/,
  /\bads manager\b/,
  /\bmeta ads manager\b/,

  /\bad campaign\b/,
  /\bads campaign\b/,
  /\bfacebook campaign\b/,
  /\binstagram campaign\b/,
  /\bpromote funnel\b/,
  /\bpromote product\b/,
  /\bpromote course\b/,
]

const CREATE_PUBLICATION_MEDIUM = [
  /\bpost para instagram\b/,
  /\bpost para facebook\b/,
  /\bpost para tiktok\b/,
  /\bpublicacion para instagram\b/,
  /\bpublicacion para facebook\b/,
  /\bpublicacion para tiktok\b/,
  /\bpublicación para instagram\b/,
  /\bpublicación para facebook\b/,
  /\bpublicación para tiktok\b/,
  /\bpost para redes\b/,
  /\bpublicacion para redes\b/,
  /\bpublicación para redes\b/,
  /\bcontenido para redes\b/,
  /\bcontenido de redes\b/,
  /\bpublicar en instagram\b/,
  /\bpublicar en facebook\b/,
  /\bpublicar en tiktok\b/,
  /\bhacer contenido para instagram\b/,
  /\bhacer contenido para facebook\b/,
  /\bhacer contenido para tiktok\b/,
  /\bcrear carrusel\b/,
  /\bcarrusel para instagram\b/,
  /\bcarrusel para tiktok\b/,
  /\bcaption para instagram\b/,
  /\bpost de instagram\b/,
  /\bpost de facebook\b/,
  /\bpost de tiktok\b/,

  /\bsocial media post\b/,
  /\binstagram post\b/,
  /\bfacebook post\b/,
  /\btiktok post\b/,
  /\bcreate carousel\b/,
]

function result(
  intent: LightweightIntent,
  confidence: LightweightIntentConfidence,
  matched?: string
): LightweightIntentResult {
  return {
    intent,
    confidence,
    shouldAutoOpen: confidence === 'high',
    matched,
  }
}

function shouldBlockLatestPublicationCarouselIntent(text: string) {
  return OPEN_LATEST_PUBLICATION_CAROUSEL_BLOCKERS.some((pattern) =>
    pattern.test(text)
  )
}

/**
 * Nuevo resolver recomendado.
 * Úsalo cuando quieras poder confirmar en confidence medium.
 */
export function resolveLightweightIntentDetailed(input: string): LightweightIntentResult {
  const text = normalizeIntentText(input)

  if (!text) {
    return result('unknown', 'low')
  }

  // HIGH confidence: acciones explícitas.
    // HIGH confidence: acciones explícitas.
  let matched: string | null = null

  matched = matchesAny(text, OPEN_LATEST_PUBLICATION_CAROUSEL_HIGH)
  if (matched && !shouldBlockLatestPublicationCarouselIntent(text)) {
    return result('open_latest_publication_carousel', 'high', matched)
  }

  matched = matchesAny(text, CREATE_ADS_CAMPAIGN_HIGH)
  if (matched) return result('open_create_ads_campaign_flow', 'high', matched)

  matched = matchesAny(text, CREATE_PUBLICATION_HIGH)
  if (matched) return result('open_create_publication_flow', 'high', matched)
    
  matched = matchesAny(text, CREATE_CONTENT_HIGH)
  if (matched) return result('open_create_content_flow', 'high', matched)

  matched = matchesAny(text, EDIT_PRODUCT_HIGH)
  if (matched) return result('open_edit_product_flow', 'high', matched)

  matched = matchesAny(text, BUILD_FUNNEL_HIGH)
  if (matched) return result('open_build_funnel_flow', 'high', matched)

  matched = matchesAny(text, CREATE_PORTRAIT_HIGH)
  if (matched) return result('open_create_portrait_flow', 'high', matched)

  matched = matchesAny(text, CREATE_FLYER_HIGH)
  if (matched) return result('open_create_flyer_flow', 'high', matched)

  matched = matchesAny(text, NAVIGATE_HIGH)
  if (matched) return result('navigate', 'high', matched)

  matched = matchesAny(text, CREATE_ADS_CAMPAIGN_MEDIUM)
  if (matched) return result('open_create_ads_campaign_flow', 'medium', matched)

  matched = matchesAny(text, CREATE_PUBLICATION_MEDIUM)
  if (matched) return result('open_create_publication_flow', 'medium', matched)

  // MEDIUM confidence: posible tarea, pero debe confirmar antes de abrir.
  matched = matchesAny(text, CREATE_CONTENT_MEDIUM)
  if (matched) return result('open_create_content_flow', 'medium', matched)

  matched = matchesAny(text, EDIT_PRODUCT_MEDIUM)
  if (matched) return result('open_edit_product_flow', 'medium', matched)

  matched = matchesAny(text, BUILD_FUNNEL_MEDIUM)
  if (matched) return result('open_build_funnel_flow', 'medium', matched)

  matched = matchesAny(text, CREATE_PORTRAIT_MEDIUM)
  if (matched) return result('open_create_portrait_flow', 'medium', matched)

  matched = matchesAny(text, CREATE_FLYER_MEDIUM)
  if (matched) return result('open_create_flyer_flow', 'medium', matched)
  
  /**
   * Si parece conversación estratégica, configuración o pregunta,
   * NO queremos abrir flujo aunque mencione “crear contenido”.
   */
  if (looksLikeDiscussion(text)) {
    return result('unknown', 'low')
  }

  return result('unknown', 'low')
}

/**
 * Mantiene compatibilidad con tu código actual.
 * IMPORTANTE:
 * Esta función solo devuelve intent high-confidence.
 * Los medium se devuelven como unknown para evitar falsos positivos.
 */
export function resolveLightweightIntent(input: string): LightweightIntent {
  const resolved = resolveLightweightIntentDetailed(input)
  return resolved.shouldAutoOpen ? resolved.intent : 'unknown'
}

export type SamanthaSystemIntent =
  | 'resume'
  | 'onboarding'
  | 'support'
  | 'subscription'
  | 'crm'
  | 'funnel'
  | 'course'
  | 'content_creation'
  | 'navigation'
  | 'general'

export type SamanthaSystemIntentResult = {
  intent: SamanthaSystemIntent
  confidence: LightweightIntentConfidence
  matched?: string
  domains: string[]
  preferredWorkerSlug?: string
  shouldUseLLM: boolean
  expressReplyKey?: string
}

function systemIntentResult(
  intent: SamanthaSystemIntent,
  confidence: LightweightIntentConfidence,
  options: Partial<SamanthaSystemIntentResult> = {}
): SamanthaSystemIntentResult {
  return {
    intent,
    confidence,
    matched: options.matched,
    domains: options.domains || [],
    preferredWorkerSlug: options.preferredWorkerSlug,
    shouldUseLLM: options.shouldUseLLM ?? true,
    expressReplyKey: options.expressReplyKey,
  }
}

const SYSTEM_RESUME = [
  /\bdonde me quede\b/,
  /\bdónde me quedé\b/,
  /\ben que me quede\b/,
  /\ben qué me quedé\b/,
  /\bpor donde iba\b/,
  /\bpor dónde iba\b/,
  /\bcontinuar\b/,
  /\bretomar\b/,
  /\bseguir\b/,
  /\bsiguiente paso\b/,
  /\bque sigue\b/,
  /\bqué sigue\b/,
]

const SYSTEM_ONBOARDING = [
  /\bonboarding\b/,
  /\bon boarding\b/,
  /\bproceso de onboarding\b/,
  /\bseguir (con )?(el )?onboarding\b/,
  /\bcontinuar (con )?(el )?onboarding\b/,
  /\bretomar (el )?onboarding\b/,
  /\bno puedo continuar\b/,
  /\bporque no puedo continuar\b/,
  /\bpor que no puedo continuar\b/,
  /\bdia \d+\b.*\bbloquead[oa]\b/,
  /\bbloquead[oa]\b.*\bdia \d+\b/,
]

const SYSTEM_SUPPORT = [
  /\bsoporte\b/,
  /\bticket\b/,
  /\bsoporte humano\b/,
  /\bequipo de soporte\b/,
  /\bcontactar soporte\b/,
  /\bcontactar con soporte\b/,
  /\bno puedo entrar\b/,
  /\bno me deja entrar\b/,
  /\bno me deja ingresar\b/,
  /\bno puedo ingresar\b/,
  /\bya pague\b/,
  /\bya pagué\b/,
  /\bclase de hoy\b/,
  /\bsala\b.*\besperando\b/,
]

const SYSTEM_SUBSCRIPTION = [
  /\bdarme de baja\b/,
  /\bdar de baja\b/,
  /\bcancelar (mi )?(suscripcion|suscripción)\b/,
  /\bquitar (mi )?(suscripcion|suscripción)\b/,
  /\bcancelar servicio\b/,
  /\bquitar servicio\b/,
]

const SYSTEM_CRM = [
  /\bprospectos?\b/,
  /\bleads?\b/,
  /\bseguimiento\b/,
  /\bcrm\b/,
  /\bwhatsapp\b/,
  /\bconversaciones?\b/,
  /\bcontactos?\b/,
  /\bmensaje(s)? por whatsapp\b/,
  /\bllevar .* a whatsapp\b/,
  /\bque me llegue(n)? .* whatsapp\b/,
]

const SYSTEM_FUNNEL = [
  /\bembudo\b/,
  /\bfunnel\b/,
  /\blanding\b/,
  /\bpagina de captura\b/,
  /\bpágina de captura\b/,
  /\beditor de embudos\b/,
  /\bbackoffice\b/,
]

const SYSTEM_COURSE = [
  /\bcurso\b/,
  /\bcursos\b/,
  /\bleccion\b/,
  /\blección\b/,
  /\bmodulo\b/,
  /\bmódulo\b/,
  /\bcapacitacion\b/,
  /\bcapacitación\b/,
  /\bretomar curso\b/,
  /\bcontinuar curso\b/,
  /\ben que curso\b/,
  /\ben qué curso\b/,
]

const SYSTEM_CONTENT = [
  /\bcrear contenido\b/,
  /\bhacer contenido\b/,
  /\bguion\b/,
  /\bguión\b/,
  /\bhilos?\b/,
  /\bthreads\b/,
  /\bpublicar\b/,
  /\bpost(s)?\b/,
  /\breels?\b/,
  /\bshorts?\b/,
  /\btiktok\b/,
  /\binstagram\b/,
  /\byoutube\b/,
  /\bfacebook\b/,
  /\bideas de contenido\b/,
]

export function resolveSamanthaSystemIntent(input: string): SamanthaSystemIntentResult {
  const text = normalizeIntentText(input)

  if (!text) {
    return systemIntentResult('general', 'low', {
      domains: ['master'],
      shouldUseLLM: true,
    })
  }

  let matched: string | null = null

  matched = matchesAny(text, SYSTEM_RESUME)
  if (matched) {
    return systemIntentResult('resume', 'high', {
      matched,
      domains: ['resume'],
      preferredWorkerSlug: 'platform_navigator',
      shouldUseLLM: false,
      expressReplyKey: 'checking_resume',
    })
  }

  matched = matchesAny(text, SYSTEM_SUBSCRIPTION)
  if (matched) {
    return systemIntentResult('subscription', 'high', {
      matched,
      domains: ['support', 'billing'],
      preferredWorkerSlug: 'platform_support',
      shouldUseLLM: false,
      expressReplyKey: 'subscription_help',
    })
  }

  matched = matchesAny(text, SYSTEM_SUPPORT)
  if (matched) {
    return systemIntentResult('support', 'high', {
      matched,
      domains: ['support'],
      preferredWorkerSlug: 'platform_support',
      shouldUseLLM: false,
      expressReplyKey: 'support_help',
    })
  }

  matched = matchesAny(text, SYSTEM_ONBOARDING)
  if (matched) {
    return systemIntentResult('onboarding', 'high', {
      matched,
      domains: ['onboarding'],
      preferredWorkerSlug: 'platform_navigator',
      shouldUseLLM: true,
      expressReplyKey: 'checking_onboarding',
    })
  }

  matched = matchesAny(text, SYSTEM_COURSE)
  if (matched) {
    return systemIntentResult('course', 'high', {
      matched,
      domains: ['courses'],
      preferredWorkerSlug: 'platform_navigator',
      shouldUseLLM: true,
      expressReplyKey: 'checking_courses',
    })
  }

  matched = matchesAny(text, SYSTEM_CONTENT)
  if (matched) {
    return systemIntentResult('content_creation', 'high', {
      matched,
      domains: ['content'],
      preferredWorkerSlug: 'content_creator',
      shouldUseLLM: true,
      expressReplyKey: 'preparing_content',
    })
  }

  const hasFunnel = matchesAny(text, SYSTEM_FUNNEL)
  const hasCrm = matchesAny(text, SYSTEM_CRM)

  if (hasFunnel && hasCrm) {
    return systemIntentResult('crm', 'high', {
      matched: `${hasFunnel}|${hasCrm}`,
      domains: ['funnels', 'crm'],
      preferredWorkerSlug: 'funnel_worker',
      shouldUseLLM: true,
      expressReplyKey: 'checking_funnel_crm',
    })
  }

  if (hasFunnel) {
    return systemIntentResult('funnel', 'high', {
      matched: hasFunnel,
      domains: ['funnels'],
      preferredWorkerSlug: 'funnel_worker',
      shouldUseLLM: true,
      expressReplyKey: 'checking_funnel',
    })
  }

  if (hasCrm) {
    return systemIntentResult('crm', 'high', {
      matched: hasCrm,
      domains: ['crm'],
      preferredWorkerSlug: 'crm_assistant',
      shouldUseLLM: true,
      expressReplyKey: 'checking_crm',
    })
  }

  return systemIntentResult('general', 'low', {
    domains: ['master'],
    shouldUseLLM: true,
  })
}